import './style.scss';
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { ActionButton } from "../../Button";
import { useLanguageContext } from "../../../contexts/LanguageContext";
import DefaultConstants from "../../../data/Constants";
import useState from "../../../hooks/useState";
import Dialog, { IDefaultDialogProps } from "../../Dialog";
import ViewAttachmentsDialog from "./ViewAttachmentsDialog";
import useDialog from "../../../hooks/useDialog";
import IServiceOrder from "../../../interfaces/IServiceOrder";
import { useServiceOrderContext } from "../../../contexts/ServiceOrderContext";
import { ServiceStatusData } from "../../../data/Enums";
import { AttachFile, Description, Forum, InfoOutlined } from '@mui/icons-material';
import CommentsDialog from './CommentsDialog';
import { useAuthContext } from '../../../contexts/AuthContext';
import StringHelper from '../../../helpers/StringHelper';
import FormOption from '../../FormOption';
import FormHelper from '../../../helpers/FormHelper';
import ServiceOrderService from '../../../services/ServiceOrderService';
import ServiceOrderInformationsDialog from './ServiceOrderInformationsDialog';
import Badge from '../../Badge';
import AddAttachmentsDialog from '../AddAttachmentsDialog';
import LogsDialog from './LogsDialog';


interface IViewServiceOrderDialog extends IDefaultDialogProps {
    serviceOrderId?: number;
}


interface IStateValues {
    serviceOrder: IServiceOrder | undefined;
    statusId: number | undefined;
    attachments: File[];
}


const ViewDialog: React.FunctionComponent<IViewServiceOrderDialog> = ({
    open,
    handleClose,
    serviceOrderId = undefined
}) => {
    const { translate } = useLanguageContext();
    const { handleRefreshServiceOrders } = useServiceOrderContext();
    const { hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        serviceOrder: undefined,
        statusId: undefined,
        attachments: []
    });

    const serviceOrderInformationsDialog = useDialog();
    const viewAttachmentsDialog = useDialog();
    const commentsDialog = useDialog();
    const logsDialog = useDialog();
    const addAttachmentsDialog = useDialog();


    useEffect(() => {
        if (serviceOrderId) {
            state.reset();
            handleRefreshSelectedServiceOrder();

            serviceOrderInformationsDialog.handleClose();
            viewAttachmentsDialog.handleClose();
            commentsDialog.handleClose();
            logsDialog.handleClose();
            addAttachmentsDialog.handleClose();
        }
        // eslint-disable-next-line
    }, [serviceOrderId]);


    const handleRefreshSelectedServiceOrder = useCallback(() => {
        handleRefreshServiceOrders((serviceOrders) => {
            state.update('serviceOrder', serviceOrders.find(s => s.id === serviceOrderId));
        });
        // eslint-disable-next-line
    }, [serviceOrderId]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (state.values.serviceOrder === undefined) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'id', state.values.serviceOrder.id);

        state.values.attachments.forEach((attachment, index) => {
            formData = FormHelper.Add(formData, `attachment[${index}]`, attachment);
        });

        ServiceOrderService.UpdateStatus(formData).then(() => {
            handleClose();
        });
        // eslint-disable-next-line
    }, [state.values, translate]);


    // Permissions
    const editStatus = useMemo(() => {
        return hasPermission(`Service.ServiceOrders.EditStatus`);
        // eslint-disable-next-line
    }, []);

    const editDepartment = useMemo(() => {
        return hasPermission(`Service.ServiceOrders.EditDepartment`);
        // eslint-disable-next-line
    }, []);

    const editPriority = useMemo(() => {
        return hasPermission(`Service.ServiceOrders.EditPriority`);
        // eslint-disable-next-line
    }, []);

    const editResponsible = useMemo(() => {
        const serviceOrder = state.values.serviceOrder;
        return hasPermission(`Service.ServiceOrders.EditResponsible`) && serviceOrder?.department?.id !== undefined;
        // eslint-disable-next-line
    }, [state.values.serviceOrder]);

    const editStartDate = useMemo(() => {
        return hasPermission(`Service.ServiceOrders.EditStartDate`);
        // eslint-disable-next-line
    }, []);

    const editEstimatedCompletionDate = useMemo(() => {
        return hasPermission(`Service.ServiceOrders.EditEstimatedCompletionDate`);
        // eslint-disable-next-line
    }, []);


    const contentActionButtons = useMemo(() => {
        const serviceOrder = state.values.serviceOrder;
        const elements = [] as JSX.Element[];

        if (serviceOrder) {
            // Informations
            elements.push(<Button 
                className='action-button'  
                onClick={() => serviceOrderInformationsDialog.handleOpen()}
                key='serviceOrderInformation'
            >
                <InfoOutlined />
                <Typography>{translate('serviceOrderInformation')}</Typography>
            </Button>);
            
            // Attachments
            elements.push(<Button 
                className='action-button' 
                onClick={() => viewAttachmentsDialog.handleOpen()}
                key='attachments'
            >
                <AttachFile />
                <Typography>{translate('attachments')}</Typography>
                <Typography className='indicator-text'>{serviceOrder.attachments?.length ?? 0}</Typography>
            </Button>);
            
            // Comments
            elements.push(<Button 
                className='action-button color-grey-6' 
                onClick={() => commentsDialog.handleOpen()}
                key='comments'
            >
                <Forum />
                <Typography>{translate('comments')}</Typography>
                <Typography className='indicator-text backgroundcolor-grey-6'>{serviceOrder.comments?.length ?? 0}</Typography>
            </Button>);

            // Log
            if (hasPermission(`Service.Reports.ServiceOrderLog`)) {
                elements.push(<Button 
                    className='action-button color-blue-grey' 
                    onClick={() => logsDialog.handleOpen()}
                    key='changeLog'
                >
                    <Description />
                    <Typography>{translate('changeLog')}</Typography>
                    <Typography className='indicator-text backgroundcolor-blue-grey'>{serviceOrder.logs?.length ?? 0}</Typography>
                </Button>);
            }
        }

        return elements;
        // eslint-disable-next-line
    }, [state.values.serviceOrder, translate, hasPermission]);


    const dialogActions = useMemo(() => {
        const elements = [] as JSX.Element[];
        elements.push(<ActionButton action='cancel' onClick={handleClose} />);

        if (editStatus) {
            elements.push(<ActionButton action='save' type='submit' />);
        }

        return elements;
        // eslint-disable-next-line
    }, [translate, editStatus]);


    if (state.values.serviceOrder === undefined) {
        return null;
    }

    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={`# ${state.values.serviceOrder.id}`}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween overflow-inherit viewserviceorderdialog-container'
            afterForm={<>
                <ServiceOrderInformationsDialog
                    open={serviceOrderInformationsDialog.open}
                    handleClose={() => serviceOrderInformationsDialog.handleClose()}
                    serviceOrder={state.values.serviceOrder}
                    handleRefreshSelectedServiceOrder={() => handleRefreshSelectedServiceOrder()}
                    editDepartment={editDepartment}
                    editPriority={editPriority}
                    editResponsible={editResponsible}
                    editStartDate={editStartDate}
                    editEstimatedCompletionDate={editEstimatedCompletionDate}
                />

                <CommentsDialog
                    open={commentsDialog.open}
                    handleClose={() => commentsDialog.handleClose()}
                    handleRefreshSelectedServiceOrder={() => handleRefreshSelectedServiceOrder()}
                    serviceOrder={state.values.serviceOrder}
                />

                <LogsDialog
                    open={logsDialog.open}
                    handleClose={() => logsDialog.handleClose()}
                    serviceOrder={state.values.serviceOrder}
                />
            </>}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                {
                    contentActionButtons.length > 0 && <Grid container className='actions-container' marginTop={3}>
                        {contentActionButtons}
                    </Grid>
                }

                {
                    editStatus && <Grid item xs={12} marginTop={4}>
                        <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' className='fontweight-600'>{translate('_changeStatusTo')}</Typography>
                            </Grid>
                
                            {
                                Object.values(ServiceStatusData)
                                    .filter(s => s.key === +(state.values.serviceOrder?.statusId ?? 0) 
                                        || hasPermission(`Service.ServiceOrders.EditStatus.${StringHelper.FirstLetterUpperCase(s.name)}`)
                                    ).map(s => {
                                        const currentServiceOrderStatus = s.key === +(state.values.serviceOrder?.statusId ?? 0);
                                        const disabled = currentServiceOrderStatus;
                
                                        return <Grid item sm={4} xs={6} key={s.key}>
                                            <FormOption
                                                name='statusId'
                                                type='radio'
                                                value={s.key}
                                                title={translate(s.name)}
                                                selected={currentServiceOrderStatus || s.key === state.values.statusId}
                                                disabled={disabled}
                                                required={state.values.statusId === undefined}
                                                onClick={() => state.update('statusId', s.key ?? 0)}
                                                Icon={s.Icon}
                                                orientation='vertical'
                                                selectedProps={{
                                                    sx: {
                                                        backgroundColor: `${s.color} !important`
                                                    }
                                                }}
                                            />
                                        </Grid>;
                                    })
                            }

                            <Grid item xs={12} className="display-flex alignitems-center justifycontent-flexend" marginTop={3}>
                                <Badge badgeContent={state.values.attachments.length} showZero={true} className='backgroundcolor-primary color-white'>
                                    <IconButton size="large" onClick={() => addAttachmentsDialog.handleOpen()} className='borderradius-default backgroundcolor-grey-2 text-color-1 display-flex gap-12'>
                                        <Typography className='fontsize-14'>{translate('addAttachments')}</Typography>
                                        <AttachFile fontSize="small" />
                                    </IconButton>
                                </Badge>
                            </Grid>
                        </Grid>
                    </Grid>
                }

                <ViewAttachmentsDialog
                    open={viewAttachmentsDialog.open}
                    handleClose={() => viewAttachmentsDialog.handleClose()}
                    attachments={state.values.serviceOrder.attachments ?? []}
                />

                <AddAttachmentsDialog
                    open={addAttachmentsDialog.open}
                    handleClose={() => addAttachmentsDialog.handleClose()}
                    attachments={state.values.attachments}
                    handleUpdateAttachments={(files) => state.update('attachments', files)}
                />
            </Grid>
        </Dialog>
    );
}

export default ViewDialog;